import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from './Header';
import { Footer } from './Footer';
import { Button } from './ui/button';
import {
  Settings,
  QrCode,
  Calendar as CalendarIcon,
  User,
  LogOut,
  Clock,
  MapPin,
  BadgeCheck,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { messeData } from '../mock';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://nextgenbusinesscup.com/api';

export const UserDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [ticketError, setTicketError] = useState('');
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [ticketInfo, setTicketInfo] = useState(null);
  const [qrImageUrl, setQrImageUrl] = useState(null);
  const [qrLoadError, setQrLoadError] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const qrBlobUrlRef = useRef(null);
  const [qrRetryKey, setQrRetryKey] = useState(0);

  const userName = user?.first_name && user?.last_name 
    ? `${user.first_name} ${user.last_name}` 
    : user?.name || (t('home.user') || 'User');

  const hasTicket = tickets.length > 0;
  const primaryTicket = hasTicket ? tickets[0] : null;

  const ticketTypeLabel = (type) => {
    const labels = {
      early_bird: 'Early Bird',
      regular: 'Regular',
      late: 'Last Call',
      discount: 'Hochschulrabatt',
      standard: 'Standard',
      sportler: 'Sportler',
      orga_team: 'Orga-Team',
      aussteller: 'Aussteller',
    };
    return labels[type] || type;
  };

  const paymentStatus = searchParams.get('payment');

  useEffect(() => {
    if (paymentStatus === 'success') {
      const t = setTimeout(() => {
        setSearchParams({}, { replace: true });
      }, 6000);
      return () => clearTimeout(t);
    }
    if (paymentStatus === 'cancel') {
      const t = setTimeout(() => {
        setSearchParams({}, { replace: true });
      }, 5000);
      return () => clearTimeout(t);
    }
  }, [paymentStatus, setSearchParams]);

  useEffect(() => {
    if (paymentStatus === 'success') {
      const refetch = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API_URL}/tickets/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setTickets(response.data || []);
        } catch (_) {}
      };
      const t = setTimeout(refetch, 2000);
      return () => clearTimeout(t);
    }
  }, [paymentStatus]);

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        setLoadingTickets(true);
        setTicketError('');
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/tickets/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTickets(response.data || []);
      } catch (err) {
        console.error('Error fetching tickets', err);
        setTicketError(t('dashboard.fehlerTickets'));
      } finally {
        setLoadingTickets(false);
      }
    };
    fetchTickets();
  }, []);

  useEffect(() => {
    if (!primaryTicket) {
      if (qrBlobUrlRef.current) {
        URL.revokeObjectURL(qrBlobUrlRef.current);
        qrBlobUrlRef.current = null;
      }
      setQrImageUrl(null);
      setQrLoadError(false);
      setQrLoading(false);
      return;
    }
    setQrLoadError(false);
    setQrLoading(true);
    const token = localStorage.getItem('token');
    axios
      .get(`${API_URL}/tickets/${primaryTicket.id}/qr`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.status !== 200 || !(res.data instanceof Blob)) {
          setQrImageUrl(null);
          setQrLoadError(true);
          return;
        }
        if (qrBlobUrlRef.current) URL.revokeObjectURL(qrBlobUrlRef.current);
        qrBlobUrlRef.current = URL.createObjectURL(res.data);
        setQrImageUrl(qrBlobUrlRef.current);
        setQrLoadError(false);
      })
      .catch(() => {
        setQrImageUrl(null);
        setQrLoadError(true);
      })
      .finally(() => setQrLoading(false));
    return () => {
      if (qrBlobUrlRef.current) {
        URL.revokeObjectURL(qrBlobUrlRef.current);
        qrBlobUrlRef.current = null;
      }
    };
  }, [primaryTicket?.id, qrRetryKey]);

  const retryQrLoad = () => {
    setQrLoadError(false);
    setQrRetryKey((k) => k + 1);
  };

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await axios.get(`${API_URL}/tickets/info`);
        setTicketInfo(res.data);
      } catch (_) {
        setTicketInfo(null);
      }
    };
    fetchInfo();
  }, []);

  const handleBuyTicket = async () => {
    try {
      setIsCreatingCheckout(true);
      setTicketError('');
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/tickets/checkout-session`,
        {
          discount_code: discountCode || undefined,
          success_url: window.location.origin + '/dashboard',
          cancel_url: window.location.origin + '/dashboard'
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const { checkout_url } = response.data || {};
      if (checkout_url) {
        window.location.href = checkout_url;
      } else {
        setTicketError(t('dashboard.fehlerCheckout'));
      }
    } catch (err) {
      console.error('Error creating checkout session', err);
      const detail = err.response?.data?.detail;
      setTicketError(detail || t('dashboard.fehlerZahlung'));
    } finally {
      setIsCreatingCheckout(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="pt-24 sm:pt-28 md:pt-32 pb-12 sm:pb-20 px-3 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header mit User-Info */}
          <div className="bg-ngbc-beige rounded-2xl shadow-lg p-8 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-ngbc-primary to-ngbc-accent rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-ngbc-dark">{userName}</h1>
                  <p className="text-ngbc-dark-text">{t('dashboard.willkommenZurueck')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {(user?.is_admin || user?.is_manager || user?.is_ticket_controller) && (
                  <Button
                    onClick={() => navigate('/admin')}
                    variant="outline"
                    className="flex items-center gap-2 border-red-500 text-red-600 hover:bg-red-50"
                  >
                    <Settings className="w-4 h-4" />
                    {user?.is_ticket_controller && !user?.is_admin && !user?.is_manager ? t('dashboard.ticketPruefen') : t('header.adminPanel')}
                  </Button>
                )}
                <Button
                  onClick={() => {
                    logout();
                    navigate('/');
                  }}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  {t('dashboard.abmelden')}
                </Button>
              </div>
            </div>
          </div>

          {/* Payment-Status nach Stripe-Redirect */}
          {paymentStatus === 'success' && (
            <div className="mb-6 flex items-center gap-3 rounded-xl border border-green-200 bg-green-50 p-4 text-green-800">
              <CheckCircle2 className="h-6 w-6 flex-shrink-0 text-green-600" />
              <div>
                <p className="font-semibold">{t('dashboard.zahlungErfolgreich')}</p>
                <p className="text-sm text-green-700">{t('dashboard.zahlungErfolgreichDesc')}</p>
              </div>
            </div>
          )}
          {paymentStatus === 'cancel' && (
            <div className="mb-6 flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-800">
              <XCircle className="h-6 w-6 flex-shrink-0 text-amber-600" />
              <div>
                <p className="font-semibold">{t('dashboard.zahlungAbgebrochen')}</p>
                <p className="text-sm text-amber-700">{t('dashboard.zahlungAbgebrochenDesc')}</p>
              </div>
            </div>
          )}

          {/* Dashboard Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Settings */}
            <div className="bg-ngbc-beige rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow cursor-pointer border-2 border-ngbc-secondary hover:border-ngbc-primary">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-ngbc-secondary/30 rounded-lg flex items-center justify-center">
                  <Settings className="w-6 h-6 text-ngbc-primary" />
                </div>
                <h3 className="text-xl font-bold text-ngbc-dark">{t('dashboard.einstellungen')}</h3>
              </div>
              <p className="text-ngbc-dark-text mb-4">
                {t('dashboard.einstellungenDesc')}
              </p>
              <Button className="w-full bg-ngbc-primary hover:bg-ngbc-accent">
                {t('dashboard.oeffnen')}
              </Button>
            </div>

            {/* Ticket & QR Code */}
            <div className="bg-ngbc-beige rounded-xl shadow-lg overflow-hidden border-2 border-ngbc-secondary hover:border-ngbc-primary transition-shadow min-w-0">
              <div className="bg-gradient-to-r from-ngbc-accent to-ngbc-primary px-6 py-3">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-ngbc-beige/20 rounded-lg flex items-center justify-center">
                    <QrCode className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">
                      {t('dashboard.eintrittsticketQr')}
                    </h3>
                    <p className="text-sm text-white/90">{messeData.name}</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
              {loadingTickets ? (
                <p className="text-ngbc-dark-text">{t('dashboard.ticketsLaden')}</p>
              ) : hasTicket ? (
                <>
                  <p className="text-ngbc-dark-text mb-4 flex items-center gap-2">
                    <BadgeCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
                    {t('dashboard.gueltigesTicket')}
                  </p>
                  <div className="bg-ngbc-secondary/30 rounded-xl p-5 border-2 border-dashed border-ngbc-primary/30">
                    <div className="flex flex-col items-center gap-6">
                      <div className="w-44 h-44 bg-ngbc-beige rounded-xl border border-ngbc-secondary flex items-center justify-center overflow-hidden shadow-sm">
                        {qrLoading && !qrImageUrl && (
                          <div className="text-ngbc-muted text-sm animate-pulse">{t('dashboard.qrWirdGeladen')}</div>
                        )}
                        {qrLoadError && !qrImageUrl && (
                          <div className="text-center p-2">
                            <p className="text-sm text-amber-700 mb-2">{t('dashboard.qrFehler')}</p>
                            <Button type="button" size="sm" variant="outline" onClick={retryQrLoad} className="text-xs">
                              {t('dashboard.erneutVersuchen')}
                            </Button>
                          </div>
                        )}
                        {qrImageUrl && (
                          <img
                            src={qrImageUrl}
                            alt={t('dashboard.qrCode')}
                            className="w-full h-full object-contain"
                          />
                        )}
                        {!qrLoading && !qrLoadError && !qrImageUrl && (
                          <div className="text-ngbc-muted text-sm">{t('dashboard.qrWirdGeladen')}</div>
                        )}
                      </div>
                      <div className="w-full text-center">
                        <p className="text-sm font-semibold text-ngbc-accent uppercase tracking-wide mb-1 leading-snug">{t('dashboard.eintrittsticket')}</p>
                        <p className="text-ngbc-dark font-bold text-lg break-words">{messeData.name}</p>
                        <p className="text-ngbc-dark-text text-sm mt-1 break-words">{messeData.datum} · {messeData.ort}</p>
                        <div className="mt-3 pt-3 border-t border-ngbc-secondary space-y-1 text-sm text-ngbc-dark break-words">
                          <p><span className="font-medium">{t('dashboard.kategorie')}:</span> {ticketTypeLabel(primaryTicket.ticket_type)}</p>
                          <p><span className="font-medium">{t('dashboard.preis')}:</span> {primaryTicket.price_eur.toFixed(2)} €</p>
                        </div>
                        <p className="text-xs text-ngbc-muted mt-2 break-words">{t('dashboard.qrAmEinlass')}</p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-ngbc-dark-text mb-2">
                    <strong>{t('dashboard.einTicketZugang')}</strong>
                  </p>
                  <p className="text-sm text-ngbc-dark-text mb-4">
                    {t('dashboard.studiRabatt')}
                  </p>
                  {ticketInfo && (
                    <div className="bg-ngbc-primary/10 rounded-lg px-3 py-2 mb-4 text-sm">
                      <span className="block text-ngbc-dark">{t('dashboard.aktuellerPreis')}: <strong>{ticketInfo.price_eur != null ? `${Number(ticketInfo.price_eur).toFixed(2)} €` : '–'}</strong></span>
                      <span className="block text-ngbc-accent font-semibold mt-1">{t('dashboard.studiPreisCode')}: {ticketInfo.student_price_eur != null ? `${Number(ticketInfo.student_price_eur).toFixed(2)} €` : '3,00 €'}</span>
                    </div>
                  )}
                  <div className="mb-4 space-y-3">
                    <label className="block text-sm font-medium text-ngbc-dark">
                      {t('dashboard.hochschulCode')}
                    </label>
                    <input
                      type="text"
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value)}
                      placeholder={t('dashboard.codePlaceholder')}
                      className="w-full px-3 py-2 border border-ngbc-dark-text rounded-lg focus:ring-2 focus:ring-ngbc-primary focus:border-transparent text-sm"
                    />
                    {ticketError && (
                      <p className="text-sm text-red-600">{ticketError}</p>
                    )}
                  </div>
                  {/* TICKET KAUF DEAKTIVIERT WEGEN STRIPE-PROBLEMEN */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-center">
                    <p className="text-amber-800 text-sm font-medium">
                      Ticketkauf vorübergehend nicht verfügbar.
                    </p>
                    <p className="text-amber-600 text-xs mt-1">
                      Wir arbeiten an einer Lösung und melden uns bald.
                    </p>
                  </div>
                  {/* 
                  <Button
                    className="w-full bg-ngbc-primary hover:bg-ngbc-accent"
                    onClick={handleBuyTicket}
                    disabled={isCreatingCheckout || (ticketInfo && ticketInfo.tickets_remaining === 0)}
                  >
                    {isCreatingCheckout ? t('dashboard.weiterleitungZahlung') : (ticketInfo && ticketInfo.tickets_remaining === 0 ? t('dashboard.ausverkauft') : t('dashboard.ticketJetztKaufen'))}
                  </Button>
                  */}
                </>
              )}
              </div>
            </div>

            {/* Zeitplan */}
            <div className="bg-ngbc-beige rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow cursor-pointer border-2 border-ngbc-secondary hover:border-ngbc-primary">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-ngbc-secondary/30 rounded-lg flex items-center justify-center">
                  <CalendarIcon className="w-6 h-6 text-ngbc-primary" />
                </div>
                <h3 className="text-xl font-bold text-ngbc-dark">{t('dashboard.meinZeitplan')}</h3>
              </div>
              <p className="text-ngbc-dark-text mb-4">
                {t('dashboard.zeitplanDesc')}
              </p>
              <div className="space-y-2 mb-4">
                <div className="flex items-center space-x-2 text-sm text-ngbc-dark-text">
                  <Clock className="w-4 h-4" />
                  <span>09:00 - Eröffnungskeynote</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-ngbc-dark-text">
                  <Clock className="w-4 h-4" />
                  <span>10:30 - Panel Diskussion</span>
                </div>
              </div>
              <Button className="w-full bg-ngbc-primary hover:bg-ngbc-accent">
                Zeitplan anzeigen
              </Button>
            </div>

            {/* Platzhalter 1 */}
            <div className="bg-ngbc-beige rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow cursor-pointer border-2 border-ngbc-secondary hover:border-ngbc-primary">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-ngbc-secondary/50 rounded-lg flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-ngbc-muted" />
                </div>
                <h3 className="text-xl font-bold text-ngbc-dark">{t('dashboard.platzhalter')} 1</h3>
              </div>
              <p className="text-ngbc-dark-text">
                {t('dashboard.weitereFunktionen')}
              </p>
            </div>

            {/* Platzhalter 2 */}
            <div className="bg-ngbc-beige rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow cursor-pointer border-2 border-ngbc-secondary hover:border-ngbc-primary">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-ngbc-secondary/50 rounded-lg flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-ngbc-muted" />
                </div>
                <h3 className="text-xl font-bold text-ngbc-dark">{t('dashboard.platzhalter')} 2</h3>
              </div>
              <p className="text-ngbc-dark-text">
                {t('dashboard.weitereFunktionen')}
              </p>
            </div>

            {/* Platzhalter 3 */}
            <div className="bg-ngbc-beige rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow cursor-pointer border-2 border-ngbc-secondary hover:border-ngbc-primary">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-ngbc-secondary/50 rounded-lg flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-ngbc-muted" />
                </div>
                <h3 className="text-xl font-bold text-ngbc-dark">{t('dashboard.platzhalter')} 3</h3>
              </div>
              <p className="text-ngbc-dark-text">
                {t('dashboard.weitereFunktionen')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};
